import React from 'react';
import { XMarkIcon, CalendarDaysIcon, ClockIcon, UserIcon, MapPinIcon } from '@heroicons/react/24/outline';

const BookingDetailModal = ({ booking, isOpen, onClose }) => {
  if (!isOpen || !booking) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-success-600 bg-success-100';
      case 'waitlist':
        return 'text-warning-600 bg-warning-100';
      case 'cancelled':
        return 'text-error-600 bg-error-100';
      case 'completed':
        return 'text-primary-600 bg-primary-100';
      case 'no_show':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmé';
      case 'waitlist':
        return 'Liste d\'attente';
      case 'cancelled':
        return 'Annulé';
      case 'completed':
        return 'Terminé';
      case 'no_show':
        return 'Absent';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateTimeString) => {
    return new Date(dateTimeString).toLocaleString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Détails de la réservation</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Statut</h3>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(booking.status)}`}>
              {getStatusText(booking.status)}
            </span>
          </div>

          {/* Client Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <UserIcon className="h-5 w-5 mr-2" />
              Informations client
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Nom complet</label>
                <p className="text-sm text-gray-900">{booking.client?.full_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <p className="text-sm text-gray-900">{booking.client?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                <p className="text-sm text-gray-900">{booking.client?.phone || 'Non renseigné'}</p>
              </div>
            </div>
          </div>

          {/* Session Information */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
              <CalendarDaysIcon className="h-5 w-5 mr-2" />
              Informations de la séance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Titre</label>
                <p className="text-sm text-gray-900">{booking.session?.title}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Date</label>
                <p className="text-sm text-gray-900">{formatDate(booking.session?.date)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Heure</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  {booking.session?.start_time} - {booking.session?.end_time}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Lieu</label>
                <p className="text-sm text-gray-900 flex items-center">
                  <MapPinIcon className="h-4 w-4 mr-1" />
                  {booking.session?.location?.name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Coach</label>
                <p className="text-sm text-gray-900">{booking.session?.coach?.user?.full_name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Capacité</label>
                <p className="text-sm text-gray-900">
                  {booking.session?.current_bookings || 0} / {booking.session?.max_capacity || 1} réservations
                </p>
              </div>
            </div>
          </div>

          {/* Service Information */}
          {booking.client_service && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Service utilisé</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Type de service</label>
                  <p className="text-sm text-gray-900">{booking.client_service?.service_type?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Statut du service</label>
                  <p className="text-sm text-gray-900">{booking.client_service?.status}</p>
                </div>
              </div>
            </div>
          )}

          {/* Booking Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Historique</h3>
            <div className="space-y-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Réservé le</label>
                <p className="text-sm text-gray-900">{formatDateTime(booking.booked_at)}</p>
              </div>
              {booking.cancelled_at && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Annulé le</label>
                  <p className="text-sm text-gray-900">{formatDateTime(booking.cancelled_at)}</p>
                </div>
              )}
              {booking.cancellation_reason && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Raison d'annulation</label>
                  <p className="text-sm text-gray-900">{booking.cancellation_reason}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingDetailModal;
